// Self-contained client for identity-service's Super Admin routes (Task 19 —
// Settings > ID Verification). Mirrors estateAdminApi.ts / billingApi.ts: a pure host
// resolver (tested without a DOM), a bare id-token header (no "Bearer " prefix, the
// same convention every sibling admin client in this app uses), and a thin `req`
// wrapper. Lives on the shared `platform` cluster, base path `/identity`, exactly
// like billing-service's `/billing` base path.

export type IdentityKeyKind = 'restricted' | 'fallback' | 'none';

export interface IdentitySettings {
  redactAfterStore: boolean;
  consentVersion: string;
  keySet: boolean;
  keyKind: IdentityKeyKind;
  webhookSet: boolean;
}

export interface IdentityStatus {
  dlqDepth: number;
  openChecks: number | null;
}

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveIdentityHost(hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/identity-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/identity`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/identity`;
  return `https://platform.api.dev.${apex}/identity`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_IDENTITY_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveIdentityHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    let code = '';
    try { code = (await res.json())?.error || ''; } catch { /* non-JSON */ }
    throw new Error(code || `identity-admin ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export const identityAdminApi = {
  settings: () => req<IdentitySettings>('/admin/settings'),

  updateSettings: (patch: { redactAfterStore?: boolean; consentVersion?: string }) =>
    req<{ redactAfterStore: boolean; consentVersion: string }>('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  // Never logs or echoes the key — the server returns only { ok, keyKind }.
  setStripeKey: (key: string) =>
    req<{ ok: true; keyKind: IdentityKeyKind }>('/admin/stripe-key', {
      method: 'PUT',
      body: JSON.stringify({ key }),
    }),

  registerWebhook: () =>
    req<{ id: string; url: string; webhookSet: boolean }>('/admin/webhook', { method: 'POST' }),

  status: () => req<IdentityStatus>('/admin/status'),
};
